import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { Sun, LogOut, LayoutDashboard, Menu } from "lucide-react";

const MOBILE_LINKS = [
  { to: "/browse", label: "Browse", id: "browse" },
  { to: "/", label: "About", id: "about" },
  { to: "/browse?category=project", label: "Projects", id: "projects" },
  { to: "/browse?category=land", label: "Land", id: "land" },
  { to: "/browse?category=connectivity", label: "Connectivity", id: "connectivity" },
  { to: "/browse?category=power", label: "Short-term Power", id: "power" },
];

const MobileMenu = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="mobile-menu-btn" aria-label="Open menu">
        <Menu className="h-5 w-5" />
      </Button>
    </SheetTrigger>
    <SheetContent side="left" data-testid="mobile-menu">
      <nav className="mt-8 flex flex-col gap-1">
        {MOBILE_LINKS.map((l) => (
          <SheetClose asChild key={l.id}>
            <Link to={l.to} data-testid={`mobile-nav-${l.id}`} className="rounded-lg px-3 py-2 font-medium hover:bg-secondary hover:text-primary transition-colors">
              {l.label}
            </Link>
          </SheetClose>
        ))}
      </nav>
    </SheetContent>
  </Sheet>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const dashHref =
    user && user.role === "admin"
      ? "/admin"
      : user && user.role === "seller"
      ? "/dashboard"
      : "/dashboard";

  return (
    <nav className="glass-nav sticky top-0 z-40">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 min-w-0">
        <MobileMenu />
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group min-w-0">
          <span className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 rounded-lg bg-primary text-primary-foreground grid place-items-center">
            <Sun className="h-5 w-5" />
          </span>
          <span className="font-display text-base sm:text-xl font-bold tracking-tight text-primary whitespace-nowrap">
            Indian <span className="text-accent">RE Trade</span>
          </span>
        </Link>
        </div>

        <div className="hidden lg:flex items-center gap-7 text-sm font-medium">
          <Link to="/browse" data-testid="nav-browse" className="hover:text-primary transition-colors">
            Browse
          </Link>
          <Link to="/browse?category=project" className="hover:text-primary transition-colors">
            Projects
          </Link>
          <Link to="/browse?category=land" className="hover:text-primary transition-colors">
            Land
          </Link>
          <Link to="/browse?category=connectivity" className="hover:text-primary transition-colors">
            Connectivity
          </Link>
          <Link to="/browse?category=power" className="hover:text-primary transition-colors">
            Short-term Power
          </Link>
          <Link to="/" data-testid="nav-about" className="hover:text-primary transition-colors">
            About
          </Link>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {user && user.email ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="nav-dashboard"
                onClick={() => navigate(dashHref)}
                className="gap-2 px-2 sm:px-3"
                aria-label="Dashboard"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="nav-logout"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="gap-2 px-2 sm:px-3"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="px-2 sm:px-3" data-testid="nav-login" onClick={() => navigate("/login")}>
                Login
              </Button>
              <Button
                size="sm"
                data-testid="nav-register"
                onClick={() => navigate("/register")}
                className="bg-primary hover:bg-primary/90 px-3"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
